import { Search, X, Filter } from "lucide-react";
import { memo } from "react";

import { SidebarSkeleton } from "./CalendarSkeleton";
import { SearchEventCard } from "./SearchEventCard";

import type { WeekFilter, ProcessedEvent } from "@/types/calendar";

interface CalendarFilterSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  searchQuery: string; // Raw search input for the input field
  onSearchChange: (query: string) => void;
  weekFilter: WeekFilter;
  onWeekFilterChange: (filter: WeekFilter) => void;
  displayedEvents: ProcessedEvent[];
  isLoading: boolean;
  selectedEvent: ProcessedEvent | null;
  onEventSelect: (event: ProcessedEvent) => void;
  isMobile?: boolean;
  debouncedSearchQuery?: string; // Debounced search query for highlighting
}

export const CalendarFilterSidebar = memo(
  ({
    isOpen,
    onToggle,
    searchQuery,
    onSearchChange,
    weekFilter,
    onWeekFilterChange,
    displayedEvents,
    isLoading,
    selectedEvent,
    onEventSelect,
    isMobile = false,
    debouncedSearchQuery = "",
  }: CalendarFilterSidebarProps) => {
    const sidebarWidth = isMobile ? "w-full" : "w-96";

    return (
      <>
        {/* Mobile Backdrop */}
        {isMobile && isOpen && (
          <div className="fixed inset-0 bg-black/25 z-[9]" onClick={onToggle} />
        )}

        {/* Toggle / Close Button — always visible, z-20 sits above sidebar z-10 */}
        <button
          onClick={onToggle}
          className={`
            absolute top-4 z-20
            border shadow-md
            p-2 transition-all duration-300
            ${
              isOpen
                ? "right-4 left-auto bg-white border-gray-300 rounded-lg hover:bg-gray-50"
                : "left-0 bg-white border-gray-300 rounded-r-lg hover:bg-gray-50"
            }
          `}
          aria-label={isOpen ? "Close filters" : "Open filters"}
        >
          {isOpen ? (
            <X className="h-5 w-5 text-gray-600" />
          ) : (
            <Filter className="h-5 w-5 text-gray-600" />
          )}
        </button>

        {/* Sidebar Container */}
        <div
          className={`
        ${isMobile ? "fixed" : "absolute"} left-0 top-0 bottom-0
        bg-white border-r shadow-lg
        transition-all duration-300 z-10
        ${isOpen ? sidebarWidth : "w-0"}
        overflow-hidden
      `}
        >
          <div className="h-full w-full flex flex-col">
            {/* Pinned close bar */}
            <div className="flex-shrink-0 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
              <h2 className="text-base font-semibold text-white">
                Filters & Events
              </h2>
              <button
                onClick={onToggle}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
              {/* Search */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Search Events
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-base bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Week Filter */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Time Period
                </h3>
                <div className="space-y-2">
                  {(["lastWeek", "thisWeek", "nextWeek"] as WeekFilter[]).map(
                    (filter) => (
                      <button
                        key={filter}
                        onClick={() => onWeekFilterChange(filter)}
                        className={`
                      w-full text-left px-3 py-2 rounded-lg transition-colors
                      ${
                        weekFilter === filter
                          ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }
                    `}
                      >
                        {filter === "lastWeek" && "Last Week"}
                        {filter === "thisWeek" && "This Week"}
                        {filter === "nextWeek" && "Next Week"}
                      </button>
                    ),
                  )}
                </div>
              </div>

              {/* Featured Events */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {searchQuery ? "Search Results" : "Featured Events"}
                  </h3>
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                    {displayedEvents.length}
                  </span>
                </div>

                {isLoading ? (
                  <SidebarSkeleton />
                ) : displayedEvents.length > 0 ? (
                  <div className="space-y-2 max-h-[50vh] md:max-h-[400px] overflow-y-auto">
                    {displayedEvents.slice(0, 15).map((event) => (
                      <SearchEventCard
                        key={event.id}
                        event={event}
                        onClick={() => onEventSelect(event)}
                        isSelected={selectedEvent?.id === event.id}
                        searchTerm={debouncedSearchQuery}
                      />
                    ))}
                    {displayedEvents.length > 15 && (
                      <div className="text-center pt-2">
                        <span className="text-sm text-gray-500">
                          +{displayedEvents.length - 15} more events
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-3xl mb-2">📅</div>
                    <p className="text-gray-500 text-sm">
                      {searchQuery
                        ? "No events match your search"
                        : "No events found"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);

CalendarFilterSidebar.displayName = "CalendarFilterSidebar";
